"use client";

import Blend from "@/components/shared/blend";
import AvailableInventoryBadge from "@/components/shared/product/available-inventory-badge";
import Potency from "@/components/shared/product/potency";
import ProductPriceDisplay from "@/components/shared/product/product-price-display";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/shared/ui/card";
import ImageWithFallback from "@/components/shared/ui/image-with-fallback";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shared/ui/tooltip";
import { GTM } from "@/lib/analytics/analytics-index";
import { cn } from "@/lib/library";
import { ShopBrowsingData, ShopInventory, ShopPrice } from "@/lib/shop/shop-types";
import { Info, Star, SwatchBook, Tag } from "lucide-react";
import Link from "next/link";
import { memo, useContext } from "react";
import AddToCartFooter from "./product-card-footer";

const MemoizedProductImage = memo(function MemoizedProductImage({
    thumbnailImageURL,
    supplierLogoURL,
    name,
    includeBorder
}: {
    thumbnailImageURL: string | null;
    supplierLogoURL: string | null;
    name: string;
    includeBorder: boolean;
}) {
    return (
        <div
            className={`relative aspect-square h-full w-full overflow-hidden ${includeBorder ? "rounded-t-[calc(1.2rem-8px)] border-8 border-white" : ""}`}
        >
            <ImageWithFallback
                unoptimized
                className="h-full w-full object-contain"
                primarySrc={thumbnailImageURL}
                fallbackSrc={supplierLogoURL}
                alt={name}
                fill
            />
        </div>
    );
});

const SaleBadge = memo(function SaleBadge({ className }: { className?: string }) {
    return (
        <div
            className={cn(
                "absolute -top-0 left-0 z-20 flex flex-row gap-1 rounded-br-lg bg-emerald-500 px-2 py-1 text-sm text-white",
                className
            )}
        >
            <Tag className="h-4 w-4" />
            Sale
        </div>
    );
});

const SampleBadge = memo(function SampleBadge({ className }: { className?: string }) {
    return (
        <div
            className={cn(
                "absolute -top-0 left-0 z-20 flex flex-row gap-1 rounded-br-lg bg-violet-600 px-2 py-1 text-sm text-white",
                className
            )}
        >
            <SwatchBook className="h-4 w-4" />
            Sample
        </div>
    );
});

const NewBadge = memo(function NewBadge({ className }: { className?: string }) {
    return (
        <div
            className={cn(
                "absolute -top-0 left-0 z-20 flex flex-row gap-1 rounded-br-lg bg-blue-500 px-2 py-1 text-sm text-white",
                className
            )}
        >
            <Star className="h-4 w-4" />
            New
        </div>
    );
});

const MemoizedLoggedInContent = memo(function LoggedInContent({
    blend,
    potency,
    isStaticPotency,
    price,
    wholesaleUnitsPerCase,
    sellByCaseOnly,
    inventory
}: {
    blend: ShopBrowsingData["blend"];
    potency: string | null;
    isStaticPotency: boolean | null;
    price: ShopPrice;
    wholesaleUnitsPerCase: number;
    sellByCaseOnly: boolean;
    inventory: ShopInventory | null;
}) {
    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center justify-between gap-1">
                <Blend type={blend} />
                {potency ? (
                    <Potency
                        potency={potency}
                        isStatic={isStaticPotency ?? false}
                        className="text-right text-sm text-gray-500"
                    />
                ) : null}
            </div>

            <div className="flex w-full flex-row items-center justify-between gap-1">
                <ProductPriceDisplay price={price} className="items-start text-left" />
                {inventory ? (
                    <AvailableInventoryBadge
                        inventory={inventory}
                        sellByCaseOnly={sellByCaseOnly}
                        wholesaleUnitsPerCase={wholesaleUnitsPerCase}
                    />
                ) : null}
            </div>
        </div>
    );
});

const MemoizedPublicContent = memo(function PublicContent({
    blend
}: {
    blend: ShopBrowsingData["blend"];
}) {
    return (
        <>
            <div className="flex items-center justify-between gap-1">
                <Blend type={blend} />
            </div>
        </>
    );
});

const MemoizedCardContent = memo(function MemoizedCardContent({
    shopData,
    productNameDisplay
}: {
    shopData: ShopBrowsingData;
    productNameDisplay: string;
}) {
    return (
        <CardContent className="flex h-full flex-col p-3">
            <div className="mb-4">
                <h3 className="">{shopData.supplierName}</h3>
                <h3
                    className="line-clamp-3 font-semibold hover:text-gray-700 hover:underline"
                    title={productNameDisplay}
                >
                    {productNameDisplay}
                </h3>
                <div className="flex flex-row justify-between text-sm ">
                    <span className="text-gray-500">{shopData.productTypeName}</span>{" "}
                    {shopData.wholesaleUnitsPerCase}ct
                </div>
            </div>

            <div className="mt-auto space-y-2">
                {shopData.price ? (
                    <MemoizedLoggedInContent
                        blend={shopData.blend}
                        potency={shopData.potency}
                        inventory={shopData.inventory}
                        isStaticPotency={shopData.isStaticPotency}
                        sellByCaseOnly={shopData.sellByCaseOnly}
                        price={shopData.price}
                        wholesaleUnitsPerCase={shopData.wholesaleUnitsPerCase}
                    />
                ) : (
                    <MemoizedPublicContent blend={shopData.blend} />
                )}
            </div>
        </CardContent>
    );
});

function ReasonTooltip({ reason }: { reason: string }) {
    const reasonArr = reason.split(";");
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className="mx-auto flex cursor-help flex-row items-center gap-1 pt-1 text-center text-xs text-gray-400 underline">
                    <Info size={12} />
                    Personalized Recommendation
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <div className="text-sm">
                    <p className="pb-2 underline">Why did we recommend this product for you?</p>
                    <ul className="list-disc pl-4">
                        {reasonArr.map((reason, i) => (
                            <li key={i}>{reason.trim()}</li>
                        ))}
                    </ul>
                </div>
            </TooltipContent>
        </Tooltip>
    );
}

export default function ProductCard({ shopData }: { shopData: ShopBrowsingData }) {
    const gtmContext = useContext(GTM.ItemContext);

    if (!shopData) return null;

    let productNameDisplay = shopData.name;
    if (productNameDisplay.includes(shopData.supplierName)) {
        productNameDisplay = productNameDisplay.replace(shopData.supplierName, "");
    }

    return (
        <div className="flex h-full flex-col justify-center">
            <Card className={`relative flex h-full flex-col overflow-hidden rounded-lg`}>
                {shopData.price?.isSale && !shopData.isSample ? (
                    <SaleBadge className={shopData.isNew ? "pl-[75px]" : ""} />
                ) : null}

                {shopData.isNew ? (
                    <NewBadge className={shopData.isSample ? "pl-[90px]" : ""} />
                ) : null}

                {shopData.isSample ? <SampleBadge /> : null}

                <Link
                    className="relative flex flex-grow flex-col "
                    href={`/${shopData.state}/product/${shopData.productID}`}
                    onClick={() => {
                        GTM.sendEvent({
                            event: "select_item",
                            ecommerce: {
                                ...gtmContext,
                                items: gtmContext.items.filter(
                                    (item) => item.item_id === shopData.productID.toString()
                                )
                            }
                        });
                    }}
                >
                    <CardHeader className="p-0">
                        <MemoizedProductImage
                            thumbnailImageURL={shopData.thumbnailImageURL}
                            supplierLogoURL={shopData.supplierLogoURL}
                            name={shopData.name}
                            includeBorder={true}
                        />
                    </CardHeader>

                    <MemoizedCardContent
                        shopData={shopData}
                        productNameDisplay={productNameDisplay}
                    />
                </Link>

                {shopData.price && shopData.inventory && !shopData.inventory.NotAuthorized ? (
                    <CardFooter className="p-0">
                        <AddToCartFooter
                            productID={shopData.productID}
                            wholesaleUnitsPerCase={shopData.wholesaleUnitsPerCase}
                            sellByCaseOnly={shopData.sellByCaseOnly}
                            retailerInventory={shopData.retailerInventory}
                            minOrderUnits={shopData.minOrderUnits}
                            unitPrice={parseFloat(shopData.price.unitPrice.toString())}
                            discount={parseFloat(shopData.price.unitDiscount.toString())}
                        />
                    </CardFooter>
                ) : shopData.inventory && shopData.inventory.NotAuthorized ? (
                    <CardFooter className="p-1">
                        <div className="w-full text-center">Not available to order</div>
                    </CardFooter>
                ) : null}
            </Card>
            {shopData.recommendationReason && (
                <ReasonTooltip reason={shopData.recommendationReason} />
            )}
        </div>
    );
}
